// ════════════════════════════════════════════════════════════════════════════
// Beskar Jobs Widget — Utilities
// ════════════════════════════════════════════════════════════════════════════

/** Escape HTML entities */
export function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Format salary range */
export function formatSalary(min: number | null, max: number | null, type = 'annual'): string {
  if (!min && !max) return '';
  const fmt = (n: number) => type === 'hourly' ? `$${n}/hr` : `$${(n / 1000).toFixed(0)}K`;
  if (min && max) return `${fmt(min)} – ${fmt(max)}`;
  if (min) return `${fmt(min)}+`;
  return `Up to ${fmt(max!)}`;
}

/** Simple email validation */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** Debounce helper */
export function debounce<T extends (...args: unknown[]) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: unknown[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
}

/** Tier label from score */
export function tierFromScore(score: number): string {
  if (score >= 86) return 'Verified';
  if (score >= 70) return 'Strongly Verified';
  if (score >= 50) return 'Partially Verified';
  if (score >= 30) return 'Limited Evidence';
  return 'Insufficient Data';
}

/** Tier CSS class */
export function tierClass(tier: string): string {
  switch (tier) {
    case 'Verified': return 'tier-verified';
    case 'Strongly Verified': return 'tier-strongly';
    case 'Partially Verified': return 'tier-partial';
    case 'Limited Evidence': return 'tier-limited';
    default: return 'tier-insufficient';
  }
}

/**
 * Extract a job slug from window.location for deep-linking.
 * Priority: pathname (/careers/<slug> or /jobs/<org>/<slug>) → hash (#<slug>).
 * Returns lowercased slug or '' if none/invalid.
 */
export function slugFromLocation(loc: { pathname: string; hash: string }): string {
  const SLUG_RE = /^[a-z0-9][a-z0-9-]*$/;
  const path = (loc.pathname || '').replace(/\/+$/, '');
  const careersMatch = path.match(/^\/careers\/([^/]+)$/i);
  if (careersMatch) {
    const s = careersMatch[1].toLowerCase();
    if (SLUG_RE.test(s)) return s;
  }
  const jobsMatch = path.match(/^\/jobs\/[^/]+\/([^/]+)$/i);
  if (jobsMatch) {
    const s = jobsMatch[1].toLowerCase();
    if (SLUG_RE.test(s)) return s;
  }
  const hash = (loc.hash || '').replace(/^#/, '').trim().toLowerCase();
  if (hash && SLUG_RE.test(hash)) return hash;
  return '';
}

/** Clearance levels for dropdown */
export const CLEARANCE_LEVELS = [
  'None',
  'Public Trust',
  'Secret',
  'Top Secret',
  'TS/SCI',
  'TS/SCI with Polygraph',
] as const;
